import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, interval, Subscription } from 'rxjs';
import { switchMap, startWith } from 'rxjs/operators';
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { initializeApp, getApps, FirebaseApp } from 'firebase/app';
import { getMessaging, getToken, onMessage, Messaging } from 'firebase/messaging';
import { ApiService } from './api.service';
import { environment } from '../../../environments/environment';
import { dedupeNotifications, getNotificationDedupKey } from '../utils/notification-dedup';

export interface AppNotification {
    id: number;
    title: string;
    body: string;
    data: Record<string, any> | null;
    is_read: boolean;
    push_sent: boolean;
    created_at: string;
}

@Injectable({ providedIn: 'root' })
export class NotificationService implements OnDestroy {

    private _unreadCount$    = new BehaviorSubject<number>(0);
    private _notifications$  = new BehaviorSubject<AppNotification[]>([]);
    private _enabled$        = new BehaviorSubject<boolean>(false);

    readonly unreadCount$   = this._unreadCount$.asObservable();
    readonly notifications$ = this._notifications$.asObservable();
    readonly enabled$       = this._enabled$.asObservable();

    private pollSub?: Subscription;
    private app?: FirebaseApp;
    private messaging?: Messaging;
    private fcmInitialized = false;
    private seenForegroundKeys = new Set<string>();

    // Poll every 30 seconds when app is visible
    private readonly POLL_INTERVAL_MS = 30_000;

    constructor(private api: ApiService) {}

    // â”€â”€ Public API â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

    /** Call after dairy user login to start polling + register FCM token. */
    async init(userType: 'dairy' | 'member' = 'dairy'): Promise<void> {
        this.startPolling(userType);
        await this.initFcm(userType);
    }

    private getMessageKey(payload: any): string {
        const data = payload?.data || {};
        const messageId = payload?.messageId || data?.message_id || data?.id || data?.notification_id || '';
        const title = String(payload?.notification?.title || data?.title || '').trim().toLowerCase();
        const body = String(payload?.notification?.body || data?.body || '').trim().toLowerCase();
        return `${messageId}::${title}::${body}`;
    }

    private addIncomingNotification(payload: any, fallbackTitle = '', fallbackBody = ''): void {
        const key = this.getMessageKey(payload);
        if (!key || this.seenForegroundKeys.has(key)) {
            return;
        }
        this.seenForegroundKeys.add(key);

        const notif: AppNotification = {
            id:         Date.now(),
            title:      payload?.notification?.title || fallbackTitle || '',
            body:       payload?.notification?.body || fallbackBody || '',
            data:       (payload?.data as any) || null,
            is_read:    false,
            push_sent:  true,
            created_at: new Date().toISOString(),
        };
        const current = this._notifications$.getValue();
        const merged = dedupeNotifications([notif, ...current]);
        const added = merged.length === current.length + 1 && merged[0]?.id === notif.id ? 1 : 0;
        this._notifications$.next(merged);
        this._unreadCount$.next(Math.max(0, this._unreadCount$.getValue() + added));
    }

    /** Fetch latest notifications once (used by bell component on open). */
    loadNotifications(): void {
        this.api.get<any>('notif/get_notifications.php').subscribe({
            next: (res) => {
                if (res?.success) {
                    this._notifications$.next(res.data || []);
                    this._unreadCount$.next(res.unread_count ?? 0);
                    this._enabled$.next(res.enabled !== false);
                }
            },
            error: () => {}
        });
    }

    loadMemberNotifications(): void {
        this.api.get<any>('notif/get_notifications_member.php').subscribe({
            next: (res) => {
                if (res?.success) {
                    this._notifications$.next(res.data || []);
                    this._unreadCount$.next(res.unread_count ?? 0);
                    this._enabled$.next(res.enabled !== false);
                }
            },
            error: () => {}
        });
    }

    markAllRead(userType: 'dairy' | 'member' = 'dairy'): void {
        const endpoint = userType === 'member'
            ? 'notif/mark_read_member.php'
            : 'notif/mark_read.php';

        this.api.post<any>(endpoint, { all: true }).subscribe({
            next: (res) => {
                if (res?.success) {
                    this._unreadCount$.next(0);
                    const updated = this._notifications$.getValue()
                        .map(n => ({ ...n, is_read: true }));
                    this._notifications$.next(updated);
                }
            },
            error: () => {}
        });
    }

    markOneRead(id: number, userType: 'dairy' | 'member' = 'dairy'): void {
        const endpoint = userType === 'member'
            ? 'notif/mark_read_member.php'
            : 'notif/mark_read.php';

        this.api.post<any>(endpoint, { notification_ids: [id] }).subscribe({
            next: (res) => {
                if (res?.success) {
                    const updated = this._notifications$.getValue()
                        .map(n => n.id === id ? { ...n, is_read: true } : n);
                    this._notifications$.next(updated);
                    const unread = updated.filter(n => !n.is_read).length;
                    this._unreadCount$.next(unread);
                }
            },
            error: () => {}
        });
    }

    stopPolling(): void {
        this.pollSub?.unsubscribe();
        this.pollSub = undefined;
        this._unreadCount$.next(0);
        this._notifications$.next([]);
        this._enabled$.next(false);
    }

    ngOnDestroy(): void {
        this.stopPolling();
    }

    // â”€â”€ Private â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

    private startPolling(userType: 'dairy' | 'member'): void {
        this.pollSub?.unsubscribe();

        const endpoint = userType === 'member'
            ? 'notif/get_notifications_member.php'
            : 'notif/get_notifications.php';

        this.pollSub = interval(this.POLL_INTERVAL_MS).pipe(
            startWith(0),
            switchMap(() => this.api.get<any>(endpoint))
        ).subscribe({
            next: (res) => {
                if (res?.success) {
                    this._enabled$.next(res.enabled !== false);
                    if (res.enabled !== false) {
                        this._notifications$.next(res.data || []);
                        this._unreadCount$.next(res.unread_count ?? 0);
                    }
                }
            },
            error: () => {}
        });
    }

    private async initFcm(userType: 'dairy' | 'member'): Promise<void> {        if (this.fcmInitialized) return;
        this.fcmInitialized = true;
        // Skip FCM on Electron (desktop app) â€” in-app polling is sufficient
        if (this.isElectron()) return;

        // On Android/iOS (Capacitor) — use native push notifications
        if (Capacitor.isNativePlatform()) {
            await this.initNativePush(userType);
            return;
        }

        // Web push
        try {
            if (!('Notification' in window)) return;

            const permission = await Notification.requestPermission();
            if (permission !== 'granted') return;

            if (!getApps().length) {
                this.app = initializeApp({
                    apiKey:            environment.firebase.apiKey,
                    projectId:         environment.firebase.projectId,
                    messagingSenderId: environment.firebase.messagingSenderId,
                    appId:             environment.firebase.appId,
                });
            } else {
                this.app = getApps()[0];
            }

            this.messaging = getMessaging(this.app);

            const token = await getToken(this.messaging, {
                vapidKey:           environment.firebase.vapidKey,
                serviceWorkerRegistration: await this.getSwRegistration(),
            });

            if (token) {
                await this.registerToken(token, 'web', userType);
            }

            // Handle foreground messages
            onMessage(this.messaging, (payload) => {
                this.addIncomingNotification(payload);
            });

        } catch (err) {
            console.error('[NotificationService] FCM init error:', err);
        }
    }

    private async initNativePush(userType: 'dairy' | 'member'): Promise<void> {
        try {
            let permStatus = await PushNotifications.checkPermissions();

            if (permStatus.receive === 'prompt') {
                const result = await PushNotifications.requestPermissions();
                permStatus = result;
            }

            if (permStatus.receive !== 'granted') return;

            await PushNotifications.removeAllListeners();
            await PushNotifications.register();

            PushNotifications.addListener('registration', async (token) => {
                await this.registerToken(token.value, 'android', userType);
            });

            PushNotifications.addListener('registrationError', (err) => {
                console.error('[NotificationService] Native push registration error:', err);
            });

            // Foreground notification — add to bell without OS notification
            PushNotifications.addListener('pushNotificationReceived', (notification) => {
                this.addIncomingNotification({
                    notification: {
                        title: notification.title || '',
                        body: notification.body || ''
                    },
                    data: notification.data || {}
                }, notification.title || '', notification.body || '');
            });

            // App opened from tapping a notification — refresh list
            PushNotifications.addListener('pushNotificationActionPerformed', () => {
                this.startPolling(userType);
            });

        } catch (err) {
            console.error('[NotificationService] Native push init error:', err);
        }
    }

    private async registerToken(token: string, deviceType: 'web' | 'android', userType: 'dairy' | 'member'): Promise<void> {
        const endpoint = userType === 'member'
            ? 'notif/register_token_member.php'
            : 'notif/register_token.php';

        this.api.post<any>(endpoint, { fcm_token: token, device_type: deviceType })
            .subscribe({ error: () => {} });
    }

    private async getSwRegistration(): Promise<ServiceWorkerRegistration | undefined> {
        if (!('serviceWorker' in navigator)) return undefined;
        try {
            return await navigator.serviceWorker.register('/firebase-messaging-sw.js');
        } catch {
            return undefined;
        }
    }

    private isElectron(): boolean {
        return !!(window as any)['electron'] ||
            navigator.userAgent.toLowerCase().includes('electron');
    }
}
